import { CalendarOutlined } from '@ant-design/icons';
import { IDateRange } from '@models';
import { DatePicker, Select, Typography } from 'antd';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import React, { useMemo } from 'react';

dayjs.extend(isBetween);

interface IProps {
  dateRange: IDateRange | undefined;
  disabledTimeSlots?: IDateRange[];
  setDateRange: (value: IDateRange) => void;
  disabled?: boolean;
}

interface IOption {
  value: string;
  label: string;
}

const { Text } = Typography;

const timeSelectOptions: IOption[] = Array.from({ length: 13 }, (_, hour) =>
  Array.from({ length: 4 }, (_, index) => {
    const hourValue = (hour + 8) % 24;
    const minuteValue = index * 15;
    const formattedHour = hourValue.toString().padStart(2, '0');
    const formattedMinute = minuteValue.toString().padStart(2, '0');
    const time = `${formattedHour}:${formattedMinute}`;
    return { value: time, label: time };
  })
).flat();
// Helper function for formatting with offset
const formatDateWithOffset = (d: dayjs.Dayjs) => d.toISOString();

export const DateTimeRangeSelect: React.FC<IProps> = ({
  dateRange,
  setDateRange,
  disabledTimeSlots,
  disabled = false,
}) => {
  const handleDateChange = (date: dayjs.Dayjs | null) => {
    if (!date) return;
    const startH = dateRange?.startAt ? dayjs(dateRange.startAt).hour() : 8;
    const startM = dateRange?.startAt ? dayjs(dateRange.startAt).minute() : 0;
    const endH = dateRange?.endAt ? dayjs(dateRange.endAt).hour() : 9;
    const endM = dateRange?.endAt ? dayjs(dateRange.endAt).minute() : 0;
    const start = dayjs(date).set('hour', startH).set('minute', startM);
    const end = dayjs(date).set('hour', endH).set('minute', endM);
    setDateRange({
      startAt: formatDateWithOffset(start),
      endAt: formatDateWithOffset(end),
    });
  };

  const handleTimeChange = (value: string, type: 'start' | 'end') => {
    const [h, m] = value.split(':');
    let start = dayjs(dateRange?.startAt);
    let end = dayjs(dateRange?.endAt);

    switch (type) {
      case 'start':
        start = start.hour(Number(h)).minute(Number(m)).second(0);
        const startIndex = timeSelectOptions.findIndex((x) => x.value === value);
        const endIndex = timeSelectOptions.findIndex((x) => x.value === end.format('HH:mm'));
        if (startIndex >= endIndex) {
          const nextEndTime = timeSelectOptions[startIndex + 1]?.value;
          if (nextEndTime) {
            const [hour, minute] = nextEndTime.split(':');
            end = end.hour(Number(hour)).minute(Number(minute)).second(0);
          }
        }
        break;
      case 'end':
        end = end.hour(Number(h)).minute(Number(m)).second(0);
        break;
    }

    setDateRange({
      startAt: formatDateWithOffset(start),
      endAt: formatDateWithOffset(end),
    });
  };

  // === Disable full days ===
  const isDateFullyBlocked = (date: dayjs.Dayjs) => {
    if (!disabledTimeSlots?.length) return false;
    return disabledTimeSlots.some((range) => {
      const start = dayjs(range.startAt).startOf('day');
      const end = dayjs(range.endAt).endOf('day');
      return date.isBetween(start, end, 'day', '[]');
    });
  };

  const disabledDate = (current: any) => {
    const todayBlocked = current < dayjs().startOf('day');
    const inDisabledList = isDateFullyBlocked(current);
    return todayBlocked || inDisabledList;
  };

  // === Disable time slots for selected date ===
  const getDisabledTimesForSelectedDate = () => {
    if (!disabledTimeSlots?.length || !dateRange?.startAt) return [];
    const selectedDate = dayjs(dateRange.startAt).startOf('day');

    const blockedRanges = disabledTimeSlots.filter((range) => {
      const startDay = dayjs(range.startAt).startOf('day');
      const endDay = dayjs(range.endAt).startOf('day');
      return selectedDate.isBetween(startDay, endDay, 'day', '[]');
    });

    const disabledTimes: string[] = [];
    blockedRanges.forEach((range) => {
      const start = dayjs(range.startAt);
      const end = dayjs(range.endAt);
      timeSelectOptions.forEach((option) => {
        const time = dayjs(selectedDate)
          .hour(Number(option.value.split(':')[0]))
          .minute(Number(option.value.split(':')[1]));
        if (time.isBetween(start, end, null, '[)')) {
          disabledTimes.push(option.value);
        }
      });
    });

    return disabledTimes;
  };

  const disabledTimes = getDisabledTimesForSelectedDate();

  // For end times, still respect ordering after start
  const index = useMemo(
    () =>
      [...timeSelectOptions].findIndex(
        (x) => x.value === dayjs(dateRange?.startAt).format('HH:mm')
      ),
    [dateRange?.startAt]
  );

  const endTimes = useMemo(
    () => [...timeSelectOptions].splice(index + 1, timeSelectOptions.length - 1),
    [index]
  );

    if (disabled) {
    const startDate = dateRange?.startAt ? dayjs(dateRange.startAt) : null;
    const endDate = dateRange?.endAt ? dayjs(dateRange.endAt) : null;
    
    return (
      <>
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <CalendarOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
        <Text strong>
          {startDate && endDate
            ? `${startDate.format('YYYY-MM-DD')}, ${startDate.format('HH:mm')} - ${endDate.format('HH:mm')}`
            : 'No date selected'}
        </Text>
      </div>
      </>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%' }}>
      <DatePicker
        inputReadOnly
        showTime={false}
        allowClear={false}
        disabledDate={disabledDate}
        format="DD/MM"
        style={{ flex: 1, minWidth: 0, height: 32 }}
        value={dateRange?.startAt ? dayjs(dateRange.startAt) : null}
        onChange={handleDateChange}
      />
      <Select
        defaultValue="8:00"
        style={{ flex: 1, minWidth: 0, height: 32 }}
        options={timeSelectOptions.map((opt) => ({
          ...opt,
          disabled: disabledTimes.includes(opt.value),
        }))}
        value={dateRange?.startAt ? dayjs(dateRange.startAt).format('HH:mm') : null}
        onChange={(e) => handleTimeChange(e, 'start')}
      />
      <Select
        defaultValue="8:15"
        style={{ flex: 1, minWidth: 0, height: 32 }}
        options={endTimes.map((opt) => ({
          ...opt,
          disabled: disabledTimes.includes(opt.value),
        }))}
        value={dateRange?.endAt ? dayjs(dateRange.endAt).format('HH:mm') : null}
        onChange={(e) => handleTimeChange(e, 'end')}
      />
    </div>
  );
};
